import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import SettingsHubPanel from '../../components/SettingsHubPanel';
import TextInput from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import NumberInput from '../../components/ui/NumberInput';
import Card from '../../components/ui/Card';
import { useApplicationSettings } from './useData';
import { useSettings } from '../../contexts/SettingsContext';
import api from '../../services/api';
import { cn } from '../../lib/utils';
import type { EmbeddedInConfigurationHub } from '../configuration-settings/models';

export type EmailNotificationSettingsPageProps = EmbeddedInConfigurationHub;

type EmailFormState = {
  enableEmailNotifications: boolean;
  mailHost: string;
  mailPort: number;
  mailSecure: boolean;
  mailUser: string;
  mailFrom: string;
  mailPasswordSet?: boolean;
};

const EmailNotificationSettingsPage: React.FC<EmailNotificationSettingsPageProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const isAdmin = user?.role === 'ADMIN';

  const { applicationSettings, isLoading, mutate: mutateApp } = useApplicationSettings();
  const { refreshSettings } = useSettings();

  const [emailForm, setEmailForm] = useState<EmailFormState>({
    enableEmailNotifications: false,
    mailHost: '',
    mailPort: 587,
    mailSecure: false,
    mailUser: '',
    mailFrom: '',
  });
  const [mailPassword, setMailPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    if (!applicationSettings) return;
    setEmailForm({
      enableEmailNotifications: applicationSettings.enableEmailNotifications ?? false,
      mailHost: applicationSettings.mailHost ?? '',
      mailPort: applicationSettings.mailPort ?? 587,
      mailSecure: applicationSettings.mailSecure ?? false,
      mailUser: applicationSettings.mailUser ?? '',
      mailFrom: applicationSettings.mailFrom ?? '',
      mailPasswordSet: applicationSettings.mailPasswordSet,
    });
  }, [applicationSettings]);

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        enableEmailNotifications: emailForm.enableEmailNotifications,
        mailHost: emailForm.mailHost,
        mailPort: emailForm.mailPort,
        mailSecure: emailForm.mailSecure,
        mailUser: emailForm.mailUser,
        mailFrom: emailForm.mailFrom,
      };
      if (mailPassword !== '') payload.mailPassword = mailPassword;
      await api.patch('/settings/application', payload);
      setMailPassword('');
      await mutateApp();
      refreshSettings();
      showSuccess('Email notification settings saved');
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    const email = testEmailTo.trim();
    if (!email) {
      showError('Please enter an email address to send the test to.');
      return;
    }
    setSendingTestEmail(true);
    try {
      await api.post('/settings/application/test-email', { email });
      showSuccess('Test email sent successfully. Check the inbox for ' + email);
      setTestEmailTo('');
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to send test email.');
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (!isAdmin) {
    if (embedded) {
      return (
        <Card variant="outlined" className="p-6 text-center">
          <p className={cn('text-sm', colors.text)}>
            Email notification settings are only available to administrators.
          </p>
        </Card>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className={cn('mb-2 text-2xl font-bold', colors.text)}>Access denied</h2>
          <p className={cn('text-sm', colors.textMuted)}>This page is only available to administrators.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    if (embedded) {
      return (
        <div className="py-10">
          <LoadingSpinner message="Loading settings…" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Loading settings…" size="large" />
      </div>
    );
  }

  const rowBox = 'flex items-center justify-between gap-4 rounded-lg border p-4';

  const saveFooter = (
    <div className="flex justify-end">
      <Button type="button" variant="default" loading={saving} disabled={saving} onClick={() => void saveEmailSettings()}>
        Save email settings
      </Button>
    </div>
  );

  const formFields = (
    <div className="space-y-4">
      <div className={cn(rowBox, colors.border)}>
        <div className="flex-1">
          <Label htmlFor="enableEmailNotifications" className="mb-1">
            Enable email notifications
          </Label>
          <p className={cn('text-xs', colors.textMuted)}>
            When enabled, the application sends transactional emails. When disabled, users are informed that notifications are
            not enabled.
          </p>
        </div>
        <Switch
          id="enableEmailNotifications"
          checked={emailForm.enableEmailNotifications}
          onChange={(e) => setEmailForm((f) => ({ ...f, enableEmailNotifications: e.target.checked }))}
        />
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <Label htmlFor="mailHost">SMTP host</Label>
          <TextInput
            id="mailHost"
            name="mailHost"
            value={emailForm.mailHost}
            onChange={(e) => setEmailForm((f) => ({ ...f, mailHost: e.target.value }))}
            placeholder="smtp.example.com"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="mailPort">SMTP port</Label>
          <NumberInput
            id="mailPort"
            name="mailPort"
            value={emailForm.mailPort}
            onChange={(e) => setEmailForm((f) => ({ ...f, mailPort: (e.target.value as number) ?? 587 }))}
            min={1}
            max={65535}
            className="mt-1"
          />
        </div>
        <div className={cn(rowBox, colors.border, 'md:col-span-2')}>
          <div className="flex-1">
            <Label htmlFor="mailSecure" className="mb-1">
              Use TLS (SSL)
            </Label>
            <p className={cn('text-xs', colors.textMuted)}>Enable for port 465</p>
          </div>
          <Switch
            id="mailSecure"
            checked={emailForm.mailSecure}
            onChange={(e) => setEmailForm((f) => ({ ...f, mailSecure: e.target.checked }))}
          />
        </div>
        <div>
          <Label htmlFor="mailUser">SMTP username</Label>
          <TextInput
            id="mailUser"
            name="mailUser"
            value={emailForm.mailUser}
            onChange={(e) => setEmailForm((f) => ({ ...f, mailUser: e.target.value }))}
            placeholder="user@example.com"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="mailPassword">SMTP password</Label>
          <TextInput
            id="mailPassword"
            name="mailPassword"
            type="password"
            value={mailPassword}
            onChange={(e) => setMailPassword(e.target.value)}
            placeholder={emailForm.mailPasswordSet ? 'Leave blank to keep current' : 'Enter password'}
            className="mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="mailFrom">From address</Label>
          <TextInput
            id="mailFrom"
            name="mailFrom"
            type="email"
            value={emailForm.mailFrom}
            onChange={(e) => setEmailForm((f) => ({ ...f, mailFrom: e.target.value }))}
            placeholder="noreply@example.com"
            className="mt-1"
          />
        </div>
      </div>
      <div className={cn('border-t pt-4', colors.border)}>
        <Label htmlFor="testEmailTo">Send test email</Label>
        <p className={cn('mb-2 mt-0.5 text-xs', colors.textMuted)}>
          Enter an address and send a message to verify SMTP settings.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <TextInput
            id="testEmailTo"
            name="testEmailTo"
            type="email"
            value={testEmailTo}
            onChange={(e) => setTestEmailTo(e.target.value)}
            placeholder="recipient@example.com"
            className="w-full min-w-[200px] max-w-xs"
          />
          <Button
            type="button"
            variant="default"
            onClick={() => void handleSendTestEmail()}
            disabled={sendingTestEmail}
            loading={sendingTestEmail}
          >
            Send test email
          </Button>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <SettingsHubPanel
        title="Email notifications"
        description="Default SMTP account for invitations, password reset, and other transactional emails sent by the application."
        helpItems={[
          {
            summary: 'Related settings elsewhere',
            content: (
              <p>
                Application-wide feature toggles and branding live under{' '}
                <strong className={cn('font-medium', colors.text)}>Settings → Application settings</strong>. Google OAuth
                for Gmail and Calendar is under <strong className={cn('font-medium', colors.text)}>Google Sync</strong>.
                Per-template wording is edited under System email templates.
              </p>
            ),
          },
        ]}
        footer={saveFooter}
        contentClassName="space-y-4"
      >
        {formFields}
      </SettingsHubPanel>
    );
  }

  const standaloneBody = (
    <div className="space-y-6">
      <p className={cn('text-xs', colors.textMuted)}>
        Default SMTP account for invitations, password reset, and other transactional emails sent by the application.
      </p>
      {formFields}
      <div className={cn('flex justify-end border-t pt-2', colors.border)}>{saveFooter}</div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <div>
          <h1 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>Email notifications</h1>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>SMTP and transactional email behavior for the whole application.</p>
        </div>
        {standaloneBody}
      </div>
    </div>
  );
};

export default EmailNotificationSettingsPage;
